/**
 * Module 'flows': the open Orgus flow format, `orgus.flow/v1` (DesignSystem 5.2).
 * The format is documented in docs/architecture/flows.md; this file is its source of truth.
 *
 * A flow is a directed acyclic graph of typed nodes. Every node has typed input and output
 * ports; an edge joins an output port to an input port of a compatible type. Flows are plain
 * YAML or JSON files, so they diff and review like code. Browser-safe, no runtime deps: the
 * YAML codec, layout, diff and live overlay live in @orgus/flows.
 */
import type { Ts } from '../index.ts';

export const FLOWS_EXT = 'flows';
export const FLOW_SCHEMA = 'orgus.flow/v1';

/** Labels for the audit entries this module writes (their action names start with "flow."). */
export const FLOWS_AUDIT_LABEL: Record<string, string> = {
  'flow.create': 'Create flow',
  'flow.save': 'Save flow',
  'flow.delete': 'Delete flow',
};

/** A JSON value: what `with:` and `x-` fields may hold. */
export type FlowValue = string | number | boolean | null | FlowValue[] | { [key: string]: FlowValue };

/** Ids of flows, nodes and ports: letters, digits, '-' and '_', starting with a letter or digit. */
export const FLOW_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
/** Custom port types and node kinds: lowercase, digits and '-'. */
export const FLOW_TYPE_RE = /^[a-z][a-z0-9-]{0,39}$/;

export interface FlowPort {
  name: string;
  /** A built-in type (FLOW_PORT_TYPES) or a custom lowercase name. */
  type: string;
  label?: string;
  /** Inputs only: accepts more than one edge (a join). */
  many?: boolean;
  /** Inputs only: the node runs without it. */
  optional?: boolean;
}

/** How live mode finds a node's agents in a running Claude Code workflow. */
export interface FlowMatch {
  /** The workflow phase title (case-insensitive). */
  phase?: string;
  /** A glob on the agent label: `tests:*`. */
  label?: string;
}

export interface FlowNode {
  id: string;
  /** A built-in kind (FLOW_KINDS) or a custom one (custom kinds declare their ports). */
  kind: string;
  title?: string;
  notes?: string;
  /** Kind-specific settings: an agent's prompt, a command's `run`, a gate's question. */
  with?: Record<string, FlowValue>;
  /** Ports added to (or overriding by name) the kind's own ports. */
  inputs?: FlowPort[];
  outputs?: FlowPort[];
  match?: FlowMatch;
  /** Pinned canvas position. Unpinned nodes are laid out automatically, left to right. */
  at?: { x: number; y: number };
  /** `x-` fields: open extension data, kept as written. */
  extra?: Record<string, FlowValue>;
}

export interface FlowEdge {
  /** `<node>.<output port>` */
  from: string;
  /** `<node>.<input port>` */
  to: string;
  label?: string;
}

export interface FlowDoc {
  schema: typeof FLOW_SCHEMA;
  id: string;
  name: string;
  description?: string;
  /** The Claude Code workflow name this flow describes; live mode overlays its runs. */
  workflow?: string;
  tags?: string[];
  nodes: FlowNode[];
  edges: FlowEdge[];
  extra?: Record<string, FlowValue>;
}

export type FlowFormat = 'yaml' | 'json';

// ---------------------------------------------------------------------------
// Port types
// ---------------------------------------------------------------------------

export interface FlowPortTypeSpec {
  label: string;
  description: string;
  /** Categorical palette slot 1..8 (tokens --hue-N); 0 is neutral. */
  hue: number;
}

export const FLOW_PORT_TYPES: Record<string, FlowPortTypeSpec> = {
  any: { label: 'Anything', description: 'Accepts or carries any type.', hue: 0 },
  signal: { label: 'Signal', description: 'Control only: go, no data.', hue: 8 },
  text: { label: 'Text', description: 'A message, a prompt or notes.', hue: 2 },
  diff: { label: 'Diff', description: 'A change to code: commits or a patch.', hue: 3 },
  report: { label: 'Report', description: 'What a step found or did: test results, a review.', hue: 5 },
  decision: { label: 'Decision', description: 'A human choice from a gate.', hue: 7 },
  artifact: { label: 'Artifact', description: 'A file or build output.', hue: 6 },
  json: { label: 'Data', description: 'Structured data.', hue: 1 },
};

/** The palette slot for any type; custom types get a stable slot from their name. */
export function flowPortHue(type: string): number {
  const known = FLOW_PORT_TYPES[type];
  if (known) return known.hue;
  let h = 0;
  for (let i = 0; i < type.length; i++) h = (h * 31 + type.charCodeAt(i)) >>> 0;
  return (h % 8) + 1;
}

export function flowPortTypeLabel(type: string): string {
  return FLOW_PORT_TYPES[type]?.label ?? type;
}

/** An output of type `out` may feed an input of type `input`. */
export function flowPortsCompatible(out: string, input: string): boolean {
  return out === input || out === 'any' || input === 'any';
}

// ---------------------------------------------------------------------------
// Node kinds
// ---------------------------------------------------------------------------

export type FlowKindCategory = 'start' | 'work' | 'gate' | 'flow' | 'end';

export interface FlowFieldSpec {
  key: string;
  label: string;
  type: 'text' | 'longtext' | 'number' | 'boolean' | 'list' | 'select';
  required?: boolean;
  options?: string[];
  placeholder?: string;
  help?: string;
  /** Shown only while another field of the node has this value (Schedule only when Starts on is schedule). */
  when?: { key: string; value: string };
  /** A setting most nodes leave alone: the inspector folds it under More. */
  advanced?: boolean;
}

export interface FlowKindSpec {
  kind: string;
  label: string;
  category: FlowKindCategory;
  description: string;
  inputs: FlowPort[];
  outputs: FlowPort[];
  fields: FlowFieldSpec[];
  /** The `with` field the canvas shows on the node. */
  summary?: string;
}

export const FLOW_GATE_KINDS = ['approval', 'review', 'pick'] as const;
export type FlowGateKind = (typeof FLOW_GATE_KINDS)[number];

export const FLOW_KINDS: Record<string, FlowKindSpec> = {
  trigger: {
    kind: 'trigger',
    label: 'Start',
    category: 'start',
    description: 'Where the flow begins',
    inputs: [],
    outputs: [{ name: 'start', type: 'signal' }],
    fields: [
      { key: 'on', label: 'Starts on', type: 'select', options: ['manual', 'schedule', 'event'] },
      { key: 'schedule', label: 'Schedule', type: 'text', placeholder: 'Cron line', when: { key: 'on', value: 'schedule' } },
      { key: 'event', label: 'Event', type: 'text', placeholder: 'Event name', when: { key: 'on', value: 'event' } },
    ],
    summary: 'on',
  },
  agent: {
    kind: 'agent',
    label: 'Agent',
    category: 'work',
    description: 'A Claude Code agent with a prompt',
    inputs: [{ name: 'in', type: 'any', many: true }],
    outputs: [{ name: 'out', type: 'report' }],
    fields: [
      { key: 'prompt', label: 'Prompt', type: 'longtext', required: true, placeholder: 'What this agent should do' },
      { key: 'model', label: 'Model', type: 'select', options: ['opus', 'sonnet', 'haiku'], help: 'Empty: the session default.' },
      { key: 'parallel', label: 'Parallel copies', type: 'number', placeholder: '1' },
      { key: 'agent', label: 'Agent type', type: 'text', placeholder: 'Default', advanced: true },
      { key: 'worktree', label: 'Own git worktree', type: 'boolean', advanced: true },
    ],
    summary: 'prompt',
  },
  command: {
    kind: 'command',
    label: 'Command',
    category: 'work',
    description: 'A shell command, such as tests',
    inputs: [{ name: 'in', type: 'any', many: true }],
    outputs: [
      { name: 'passed', type: 'report' },
      { name: 'failed', type: 'report' },
    ],
    fields: [
      { key: 'run', label: 'Command', type: 'text', required: true, placeholder: 'pnpm test' },
      { key: 'cwd', label: 'Folder', type: 'text', placeholder: 'The checkout root', advanced: true },
      { key: 'timeout', label: 'Timeout', type: 'text', placeholder: 'No limit', advanced: true },
    ],
    summary: 'run',
  },
  approval: {
    kind: 'approval',
    label: 'Approval',
    category: 'gate',
    description: 'A person approves or rejects',
    inputs: [{ name: 'in', type: 'any' }],
    outputs: [
      { name: 'approved', type: 'any' },
      { name: 'rejected', type: 'decision' },
    ],
    fields: [
      { key: 'question', label: 'Question', type: 'text', required: true, placeholder: 'Ship this change?' },
      { key: 'who', label: 'Who decides', type: 'text', placeholder: 'owner' },
      { key: 'timeout', label: 'Times out after', type: 'text', placeholder: 'Never', advanced: true },
    ],
    summary: 'question',
  },
  review: {
    kind: 'review',
    label: 'Review',
    category: 'gate',
    description: 'A person accepts or sends back',
    inputs: [{ name: 'in', type: 'any' }],
    outputs: [
      { name: 'accepted', type: 'any' },
      { name: 'changes', type: 'text', label: 'changes requested' },
    ],
    fields: [
      { key: 'who', label: 'Reviewer', type: 'text', placeholder: 'owner' },
      { key: 'checklist', label: 'Checklist', type: 'list', placeholder: 'One item per line' },
    ],
    summary: 'who',
  },
  pick: {
    kind: 'pick',
    label: 'Pick one',
    category: 'gate',
    description: 'A person picks one path',
    inputs: [{ name: 'in', type: 'any' }],
    outputs: [],
    fields: [
      { key: 'question', label: 'Question', type: 'text', required: true, placeholder: 'Which direction?' },
      { key: 'options', label: 'Options', type: 'list', required: true, placeholder: 'One option per line', help: 'At least two. Each option becomes an output.' },
      { key: 'who', label: 'Who decides', type: 'text', placeholder: 'owner' },
    ],
    summary: 'question',
  },
  subflow: {
    kind: 'subflow',
    label: 'Subflow',
    category: 'flow',
    description: 'Another flow as one step',
    inputs: [],
    outputs: [],
    fields: [{ key: 'flow', label: 'Flow', type: 'text', required: true, placeholder: 'flow id' }],
    summary: 'flow',
  },
  output: {
    kind: 'output',
    label: 'Finish',
    category: 'end',
    description: 'Where the flow ends',
    inputs: [{ name: 'result', type: 'any', many: true, optional: true }],
    outputs: [],
    fields: [],
  },
};

export const FLOW_KIND_ORDER = ['trigger', 'agent', 'command', 'approval', 'review', 'pick', 'subflow', 'output'] as const;

export function isFlowGateKind(kind: string): kind is FlowGateKind {
  return (FLOW_GATE_KINDS as readonly string[]).includes(kind);
}

// ---------------------------------------------------------------------------
// Ports, refs and interfaces
// ---------------------------------------------------------------------------

/** What a flow offers when used as a subflow. */
export interface FlowInterface {
  inputs: FlowPort[];
  outputs: FlowPort[];
}

/** Resolves a subflow reference to the referenced flow's interface. */
export type FlowLibrary = (flowId: string) => FlowInterface | undefined;

export const noFlowLibrary: FlowLibrary = () => undefined;

export interface FlowPortRef {
  node: string;
  port: string;
}

export function parseFlowRef(ref: string): FlowPortRef | null {
  const i = ref.indexOf('.');
  if (i <= 0 || i === ref.length - 1) return null;
  return { node: ref.slice(0, i), port: ref.slice(i + 1) };
}

export function formatFlowRef(r: FlowPortRef): string {
  return `${r.node}.${r.port}`;
}

export function flowEdgeKey(e: Pick<FlowEdge, 'from' | 'to'>): string {
  return `${e.from}->${e.to}`;
}

/** An option label as a port name: 'Ship it now' -> 'ship-it-now'. */
export function flowPortSlug(label: string): string {
  const s = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return s || 'option';
}

function stringList(v: FlowValue | undefined): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim());
  if (typeof v === 'string') return v.split('\n').map((x) => x.trim()).filter(Boolean);
  return [];
}

/** A pick gate's options, as written. */
export function flowPickOptions(node: Pick<FlowNode, 'with'>): string[] {
  return stringList(node.with?.options);
}

function mergePorts(base: FlowPort[], over: FlowPort[] | undefined): FlowPort[] {
  if (!over?.length) return base;
  const out = base.map((p) => ({ ...p }));
  for (const p of over) {
    const i = out.findIndex((x) => x.name === p.name);
    if (i >= 0) out[i] = { ...out[i], ...p };
    else out.push({ ...p });
  }
  return out;
}

/** A node's effective ports: its kind's ports, then what it declares itself. */
export function resolveFlowPorts(node: FlowNode, lib: FlowLibrary = noFlowLibrary): FlowInterface {
  const spec = FLOW_KINDS[node.kind];
  let inputs = spec?.inputs ?? [];
  let outputs = spec?.outputs ?? [];
  if (node.kind === 'pick') {
    const seen = new Set<string>();
    outputs = flowPickOptions(node).map((label) => {
      let name = flowPortSlug(label);
      for (let i = 2; seen.has(name); i++) name = `${flowPortSlug(label)}-${i}`;
      seen.add(name);
      return { name, type: 'decision', label };
    });
  }
  if (node.kind === 'subflow') {
    const ref = node.with?.flow;
    const iface = typeof ref === 'string' ? lib(ref) : undefined;
    if (iface) {
      inputs = iface.inputs.map((p) => ({ ...p }));
      outputs = iface.outputs.map((p) => ({ ...p }));
    }
  }
  return { inputs: mergePorts(inputs, node.inputs), outputs: mergePorts(outputs, node.outputs) };
}

/** A flow's interface as a subflow: its start outputs in, its finish inputs out. */
export function flowInterface(doc: FlowDoc, lib: FlowLibrary = noFlowLibrary): FlowInterface {
  const inputs: FlowPort[] = [];
  const outputs: FlowPort[] = [];
  for (const n of doc.nodes) {
    if (n.kind === 'trigger') for (const p of resolveFlowPorts(n, lib).outputs) if (!inputs.some((x) => x.name === p.name)) inputs.push({ name: p.name, type: p.type, ...(p.label ? { label: p.label } : {}) });
    if (n.kind === 'output') for (const p of resolveFlowPorts(n, lib).inputs) if (!outputs.some((x) => x.name === p.name)) outputs.push({ name: p.name, type: p.type, ...(p.label ? { label: p.label } : {}) });
  }
  return { inputs: inputs.map((p) => ({ ...p, many: true, optional: true })), outputs };
}

// ---------------------------------------------------------------------------
// Reading and writing the document object
// ---------------------------------------------------------------------------

export type FlowPath = (string | number)[];

export interface FlowIssue {
  level: 'error' | 'warning';
  message: string;
  /** Where in the document: ['nodes', 2, 'kind']. */
  path: FlowPath;
  nodeId?: string;
  /** Index into `edges`. */
  edge?: number;
  /** 1-based, when the codec could place it in the text. */
  line?: number;
  col?: number;
}

const DOC_KEYS = ['schema', 'id', 'name', 'description', 'workflow', 'tags', 'nodes', 'edges'];
const NODE_KEYS = ['id', 'kind', 'title', 'notes', 'with', 'inputs', 'outputs', 'match', 'at'];
const PORT_KEYS = ['name', 'type', 'label', 'many', 'optional'];
const EDGE_KEYS = ['from', 'to', 'label'];

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function isFlowValue(v: unknown, depth = 0): v is FlowValue {
  if (depth > 32) return false;
  if (v === null || typeof v === 'string' || typeof v === 'boolean') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  if (Array.isArray(v)) return v.every((x) => isFlowValue(x, depth + 1));
  if (isObj(v)) return Object.values(v).every((x) => isFlowValue(x, depth + 1));
  return false;
}

class Reader {
  issues: FlowIssue[] = [];
  err(path: FlowPath, message: string, extra: Partial<FlowIssue> = {}): void {
    this.issues.push({ level: 'error', message, path, ...extra });
  }
  str(o: Record<string, unknown>, key: string, path: FlowPath, required: boolean): string | undefined {
    const v = o[key];
    if (v === undefined || v === null) {
      if (required) this.err([...path, key], `${key} is missing.`);
      return undefined;
    }
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v !== 'string') {
      this.err([...path, key], `${key} must be text.`);
      return undefined;
    }
    return v;
  }
  extraKeys(o: Record<string, unknown>, known: string[], path: FlowPath, what: string, allowExtra: boolean): Record<string, FlowValue> | undefined {
    let extra: Record<string, FlowValue> | undefined;
    for (const [k, v] of Object.entries(o)) {
      if (known.includes(k)) continue;
      if (allowExtra && k.startsWith('x-')) {
        if (!isFlowValue(v)) this.err([...path, k], `${k} must hold plain data.`);
        else (extra ??= {})[k] = v;
        continue;
      }
      this.err([...path, k], `${what} has no field '${k}'.${allowExtra ? ' Custom fields start with x-.' : ''}`);
    }
    return extra;
  }
  ports(v: unknown, path: FlowPath): FlowPort[] | undefined {
    if (v === undefined || v === null) return undefined;
    if (!Array.isArray(v)) {
      this.err(path, 'Ports must be a list.');
      return undefined;
    }
    const out: FlowPort[] = [];
    v.forEach((p, i) => {
      const pp = [...path, i];
      // Shorthand: '- report' or '- name: type'.
      if (typeof p === 'string') {
        const m = /^([^:\s]+)\s*:\s*([^\s]+)$/.exec(p);
        if (m) out.push({ name: m[1]!, type: m[2]! });
        else out.push({ name: p, type: 'any' });
        return;
      }
      if (!isObj(p)) return this.err(pp, 'A port is a map with name and type.');
      this.extraKeys(p, PORT_KEYS, pp, 'A port', false);
      const name = this.str(p, 'name', pp, true);
      const type = this.str(p, 'type', pp, false) ?? 'any';
      if (!name) return;
      const port: FlowPort = { name, type };
      const label = this.str(p, 'label', pp, false);
      if (label) port.label = label;
      if (p.many === true) port.many = true;
      else if (p.many !== undefined && p.many !== false) this.err([...pp, 'many'], 'many must be true or false.');
      if (p.optional === true) port.optional = true;
      else if (p.optional !== undefined && p.optional !== false) this.err([...pp, 'optional'], 'optional must be true or false.');
      out.push(port);
    });
    return out;
  }
}

/**
 * Read a parsed YAML/JSON object as a flow. Structural problems (wrong types, unknown
 * fields) are errors with paths, and any of them means no flow: a half-read file must never
 * be written back over the original. Graph problems (cycles, bad edges) come from validateFlow.
 */
export function flowFromObject(raw: unknown): { flow: FlowDoc | null; issues: FlowIssue[] } {
  const r = new Reader();
  if (!isObj(raw)) {
    r.err([], 'A flow file is a map with schema, id, name, nodes and edges.');
    return { flow: null, issues: r.issues };
  }
  const schema = r.str(raw, 'schema', [], false);
  if (schema !== undefined && schema !== FLOW_SCHEMA) r.err(['schema'], `This reader knows ${FLOW_SCHEMA}, not ${schema}.`);
  const id = r.str(raw, 'id', [], true) ?? '';
  const name = r.str(raw, 'name', [], false) ?? id;
  const extra = r.extraKeys(raw, DOC_KEYS, [], 'A flow', true);
  const doc: FlowDoc = { schema: FLOW_SCHEMA, id, name, nodes: [], edges: [] };
  const description = r.str(raw, 'description', [], false);
  if (description) doc.description = description;
  const workflow = r.str(raw, 'workflow', [], false);
  if (workflow) doc.workflow = workflow;
  if (raw.tags !== undefined && raw.tags !== null) {
    if (Array.isArray(raw.tags) && raw.tags.every((t) => typeof t === 'string')) {
      if (raw.tags.length) doc.tags = raw.tags as string[];
    } else r.err(['tags'], 'tags must be a list of words.');
  }
  if (extra) doc.extra = extra;

  const nodes = raw.nodes ?? [];
  if (!Array.isArray(nodes)) r.err(['nodes'], 'nodes must be a list.');
  else
    nodes.forEach((n, i) => {
      const p: FlowPath = ['nodes', i];
      if (!isObj(n)) return r.err(p, 'A node is a map with at least id and kind.');
      const nid = r.str(n, 'id', p, true);
      const kind = r.str(n, 'kind', p, true);
      if (!nid || !kind) return;
      const node: FlowNode = { id: nid, kind };
      const nx = r.extraKeys(n, NODE_KEYS, p, 'A node', true);
      const title = r.str(n, 'title', p, false);
      if (title) node.title = title;
      const notes = r.str(n, 'notes', p, false);
      if (notes) node.notes = notes;
      if (n.with !== undefined && n.with !== null) {
        if (!isObj(n.with) || !isFlowValue(n.with)) r.err([...p, 'with'], 'with must be a map of settings.');
        else if (Object.keys(n.with).length) node.with = n.with as Record<string, FlowValue>;
      }
      const inputs = r.ports(n.inputs, [...p, 'inputs']);
      if (inputs?.length) node.inputs = inputs;
      const outputs = r.ports(n.outputs, [...p, 'outputs']);
      if (outputs?.length) node.outputs = outputs;
      if (n.match !== undefined && n.match !== null) {
        if (!isObj(n.match)) r.err([...p, 'match'], 'match is a map with phase or label.');
        else {
          r.extraKeys(n.match, ['phase', 'label'], [...p, 'match'], 'match', false);
          const m: FlowMatch = {};
          const phase = r.str(n.match, 'phase', [...p, 'match'], false);
          const label = r.str(n.match, 'label', [...p, 'match'], false);
          if (phase) m.phase = phase;
          if (label) m.label = label;
          if (m.phase || m.label) node.match = m;
        }
      }
      if (n.at !== undefined && n.at !== null) {
        const at = n.at;
        if (isObj(at) && typeof at.x === 'number' && typeof at.y === 'number' && Number.isFinite(at.x) && Number.isFinite(at.y)) node.at = { x: Math.round(at.x), y: Math.round(at.y) };
        else if (Array.isArray(at) && at.length === 2 && at.every((x) => typeof x === 'number' && Number.isFinite(x))) node.at = { x: Math.round(at[0] as number), y: Math.round(at[1] as number) };
        else r.err([...p, 'at'], 'at is { x, y } in canvas units.');
      }
      if (nx) node.extra = nx;
      doc.nodes.push(node);
    });

  const edges = raw.edges ?? [];
  if (!Array.isArray(edges)) r.err(['edges'], 'edges must be a list.');
  else
    edges.forEach((e, i) => {
      const p: FlowPath = ['edges', i];
      // Shorthand: '- a.out -> b.in'.
      if (typeof e === 'string') {
        const m = /^\s*(\S+)\s*->\s*(\S+)\s*$/.exec(e);
        if (m) doc.edges.push({ from: m[1]!, to: m[2]! });
        else r.err(p, "An edge is { from: node.port, to: node.port } or 'node.port -> node.port'.");
        return;
      }
      if (!isObj(e)) return r.err(p, 'An edge is a map with from and to.');
      r.extraKeys(e, EDGE_KEYS, p, 'An edge', false);
      const from = r.str(e, 'from', p, true);
      const to = r.str(e, 'to', p, true);
      if (!from || !to) return;
      const edge: FlowEdge = { from, to };
      const label = r.str(e, 'label', p, false);
      if (label) edge.label = label;
      doc.edges.push(edge);
    });

  return { flow: r.issues.length ? null : doc, issues: r.issues };
}

function portObject(p: FlowPort): Record<string, unknown> {
  const o: Record<string, unknown> = { name: p.name, type: p.type };
  if (p.label) o.label = p.label;
  if (p.many) o.many = true;
  if (p.optional) o.optional = true;
  return o;
}

/** The canonical document object: fixed key order, nothing empty. flowFromObject reads it back unchanged. */
export function flowToObject(doc: FlowDoc): Record<string, unknown> {
  const o: Record<string, unknown> = { schema: FLOW_SCHEMA, id: doc.id, name: doc.name };
  if (doc.description) o.description = doc.description;
  if (doc.workflow) o.workflow = doc.workflow;
  if (doc.tags?.length) o.tags = [...doc.tags];
  if (doc.extra) for (const [k, v] of Object.entries(doc.extra)) o[k] = v;
  o.nodes = doc.nodes.map((n) => {
    const x: Record<string, unknown> = { id: n.id, kind: n.kind };
    if (n.title) x.title = n.title;
    if (n.notes) x.notes = n.notes;
    if (n.with && Object.keys(n.with).length) x.with = n.with;
    if (n.inputs?.length) x.inputs = n.inputs.map(portObject);
    if (n.outputs?.length) x.outputs = n.outputs.map(portObject);
    if (n.match && (n.match.phase || n.match.label)) x.match = { ...(n.match.phase ? { phase: n.match.phase } : {}), ...(n.match.label ? { label: n.match.label } : {}) };
    if (n.at) x.at = { x: n.at.x, y: n.at.y };
    if (n.extra) for (const [k, v] of Object.entries(n.extra)) x[k] = v;
    return x;
  });
  o.edges = doc.edges.map((e) => ({ from: e.from, to: e.to, ...(e.label ? { label: e.label } : {}) }));
  return o;
}

// ---------------------------------------------------------------------------
// Validation and connections
// ---------------------------------------------------------------------------

export function flowNodeTitle(n: Pick<FlowNode, 'id' | 'kind' | 'title'>): string {
  return n.title || FLOW_KINDS[n.kind]?.label || n.id;
}

function hasValue(v: FlowValue | undefined): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/** Nodes in topological order; `cyclic`: the nodes it could not order (on or after a loop); `loops`: the nodes on one. */
export function flowTopoOrder(doc: Pick<FlowDoc, 'nodes' | 'edges'>): { order: string[]; cyclic: string[]; loops: string[] } {
  const ids = doc.nodes.map((n) => n.id);
  const known = new Set(ids);
  const indeg = new Map(ids.map((id) => [id, 0]));
  const next = new Map<string, string[]>(ids.map((id) => [id, []]));
  for (const e of doc.edges) {
    const a = parseFlowRef(e.from)?.node;
    const b = parseFlowRef(e.to)?.node;
    if (!a || !b || !known.has(a) || !known.has(b)) continue;
    next.get(a)!.push(b);
    indeg.set(b, (indeg.get(b) ?? 0) + 1);
  }
  const queue = ids.filter((id) => indeg.get(id) === 0);
  const order: string[] = [];
  while (queue.length) {
    const id = queue.shift()!;
    order.push(id);
    for (const b of next.get(id) ?? []) {
      const d = (indeg.get(b) ?? 0) - 1;
      indeg.set(b, d);
      if (d === 0) queue.push(b);
    }
  }
  const done = new Set(order);
  const cyclic = ids.filter((id) => !done.has(id));
  // Of the rest, the ones that reach themselves are on a loop; the others only follow one.
  const rest = new Set(cyclic);
  const loops = cyclic.filter((start) => {
    const seen = new Set<string>();
    const stack = [...(next.get(start) ?? [])];
    while (stack.length) {
      const id = stack.pop()!;
      if (id === start) return true;
      if (seen.has(id) || !rest.has(id)) continue;
      seen.add(id);
      stack.push(...(next.get(id) ?? []));
    }
    return false;
  });
  return { order, cyclic, loops };
}

/** True when `to` can already reach `from` (so from -> to would close a cycle). */
function reaches(doc: Pick<FlowDoc, 'edges'>, start: string, target: string): boolean {
  const seen = new Set<string>();
  const stack = [start];
  while (stack.length) {
    const id = stack.pop()!;
    if (id === target) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const e of doc.edges) {
      if (parseFlowRef(e.from)?.node === id) {
        const b = parseFlowRef(e.to)?.node;
        if (b) stack.push(b);
      }
    }
  }
  return false;
}

export type FlowConnectResult = { ok: true } | { ok: false; reason: string };

/**
 * Whether an edge from an output port to an input port may exist. The canvas asks this while
 * you drag, so an invalid connection can never be made; validateFlow asks it of every edge.
 */
export function canConnectFlow(doc: FlowDoc, lib: FlowLibrary, from: FlowPortRef, to: FlowPortRef, ignoreEdge?: number): FlowConnectResult {
  const a = doc.nodes.find((n) => n.id === from.node);
  const b = doc.nodes.find((n) => n.id === to.node);
  if (!a || !b) return { ok: false, reason: 'That node does not exist.' };
  if (a.id === b.id) return { ok: false, reason: 'A node cannot feed itself.' };
  const out = resolveFlowPorts(a, lib).outputs.find((p) => p.name === from.port);
  if (!out) return { ok: false, reason: `${flowNodeTitle(a)} has no output '${from.port}'.` };
  const input = resolveFlowPorts(b, lib).inputs.find((p) => p.name === to.port);
  if (!input) return { ok: false, reason: `${flowNodeTitle(b)} has no input '${to.port}'.` };
  if (!flowPortsCompatible(out.type, input.type)) return { ok: false, reason: `${flowPortTypeLabel(out.type)} cannot feed ${flowPortTypeLabel(input.type)}.` };
  const fromRef = formatFlowRef(from);
  const toRef = formatFlowRef(to);
  const others = doc.edges.filter((_, i) => i !== ignoreEdge);
  if (others.some((e) => e.from === fromRef && e.to === toRef)) return { ok: false, reason: 'These ports are already connected.' };
  if (!input.many && others.some((e) => e.to === toRef)) return { ok: false, reason: `${flowNodeTitle(b)}.${to.port} takes one connection.` };
  if (reaches({ edges: others }, b.id, a.id)) return { ok: false, reason: 'That would make a loop. Flows run left to right.' };
  return { ok: true };
}

/** Every problem with a flow, for the canvas (on the node) and the code view (on the line). */
export function validateFlow(doc: FlowDoc, lib: FlowLibrary = noFlowLibrary): FlowIssue[] {
  const issues: FlowIssue[] = [];
  const err = (path: FlowPath, message: string, extra: Partial<FlowIssue> = {}) => issues.push({ level: 'error', message, path, ...extra });
  const warn = (path: FlowPath, message: string, extra: Partial<FlowIssue> = {}) => issues.push({ level: 'warning', message, path, ...extra });

  if (!FLOW_ID_RE.test(doc.id)) err(['id'], 'The flow id may use letters, digits, - and _ only.');
  if (!doc.name.trim()) err(['name'], 'The flow needs a name.');

  const seen = new Set<string>();
  doc.nodes.forEach((n, i) => {
    const p: FlowPath = ['nodes', i];
    if (!FLOW_ID_RE.test(n.id)) err([...p, 'id'], `'${n.id}' is not a valid node id: letters, digits, - and _ only.`, { nodeId: n.id });
    if (seen.has(n.id)) err([...p, 'id'], `Two nodes are called '${n.id}'.`, { nodeId: n.id });
    seen.add(n.id);
    const spec = FLOW_KINDS[n.kind];
    if (!spec) {
      if (!FLOW_TYPE_RE.test(n.kind)) err([...p, 'kind'], `'${n.kind}' is not a valid kind.`, { nodeId: n.id });
      else if (!n.inputs?.length && !n.outputs?.length) warn([...p, 'kind'], `Custom kind '${n.kind}' declares no ports, so nothing can connect to it.`, { nodeId: n.id });
    } else {
      for (const f of spec.fields) if (f.required && !hasValue(n.with?.[f.key])) err([...p, 'with', f.key], `${f.label} is required.`, { nodeId: n.id });
      for (const f of spec.fields) {
        const v = n.with?.[f.key];
        if (v === undefined || v === null) continue;
        if (f.type === 'number' && (typeof v !== 'number' || v < 1)) err([...p, 'with', f.key], `${f.label} must be a number of at least 1.`, { nodeId: n.id });
        if (f.type === 'boolean' && typeof v !== 'boolean') err([...p, 'with', f.key], `${f.label} must be true or false.`, { nodeId: n.id });
        if (f.type === 'select' && f.options && typeof v === 'string' && v && !f.options.includes(v) && f.key !== 'model') warn([...p, 'with', f.key], `${f.label} '${v}' is not one of ${f.options.join(', ')}.`, { nodeId: n.id });
      }
    }
    if (n.kind === 'pick' && flowPickOptions(n).length < 2) err([...p, 'with', 'options'], 'Pick one needs at least two options.', { nodeId: n.id });
    if (n.kind === 'subflow') {
      const ref = n.with?.flow;
      if (typeof ref === 'string' && ref) {
        if (ref === doc.id) err([...p, 'with', 'flow'], 'A flow cannot use itself as a subflow.', { nodeId: n.id });
        else if (!lib(ref)) err([...p, 'with', 'flow'], `No flow called '${ref}'.`, { nodeId: n.id });
      }
    }
    const ports = resolveFlowPorts(n, lib);
    for (const [side, list] of [['inputs', ports.inputs], ['outputs', ports.outputs]] as const) {
      const names = new Set<string>();
      list.forEach((port) => {
        if (!FLOW_ID_RE.test(port.name)) err([...p, side], `'${port.name}' is not a valid port name.`, { nodeId: n.id });
        if (port.type !== 'any' && !FLOW_PORT_TYPES[port.type] && !FLOW_TYPE_RE.test(port.type)) err([...p, side], `'${port.type}' is not a valid port type.`, { nodeId: n.id });
        if (names.has(port.name)) err([...p, side], `Two ${side === 'inputs' ? 'inputs' : 'outputs'} are called '${port.name}'.`, { nodeId: n.id });
        names.add(port.name);
      });
    }
  });

  const byId = new Map(doc.nodes.map((n) => [n.id, n]));
  const pairs = new Set<string>();
  const into = new Map<string, number>();
  doc.edges.forEach((e, i) => {
    const p: FlowPath = ['edges', i];
    const from = parseFlowRef(e.from);
    const to = parseFlowRef(e.to);
    if (!from) return err([...p, 'from'], `'${e.from}' is not node.port.`, { edge: i });
    if (!to) return err([...p, 'to'], `'${e.to}' is not node.port.`, { edge: i });
    const a = byId.get(from.node);
    const b = byId.get(to.node);
    if (!a) return err([...p, 'from'], `No node called '${from.node}'.`, { edge: i });
    if (!b) return err([...p, 'to'], `No node called '${to.node}'.`, { edge: i });
    const out = resolveFlowPorts(a, lib).outputs.find((x) => x.name === from.port);
    const input = resolveFlowPorts(b, lib).inputs.find((x) => x.name === to.port);
    if (!out) return err([...p, 'from'], `${flowNodeTitle(a)} has no output '${from.port}'.`, { edge: i, nodeId: a.id });
    if (!input) return err([...p, 'to'], `${flowNodeTitle(b)} has no input '${to.port}'.`, { edge: i, nodeId: b.id });
    if (!flowPortsCompatible(out.type, input.type)) err(p, `${flowPortTypeLabel(out.type)} cannot feed ${flowPortTypeLabel(input.type)} (${e.from} to ${e.to}).`, { edge: i, nodeId: b.id });
    const k = flowEdgeKey(e);
    if (pairs.has(k)) err(p, `${e.from} and ${e.to} are connected twice.`, { edge: i });
    pairs.add(k);
    into.set(e.to, (into.get(e.to) ?? 0) + 1);
    if (!input.many && (into.get(e.to) ?? 0) > 1) err(p, `${e.to} takes one connection.`, { edge: i, nodeId: b.id });
    if (a.id === b.id) err(p, 'A node cannot feed itself.', { edge: i, nodeId: a.id });
  });

  const { loops } = flowTopoOrder(doc);
  for (const id of loops) err(['nodes', doc.nodes.findIndex((n) => n.id === id)], `${flowNodeTitle(byId.get(id)!)} is on a loop. Flows run left to right.`, { nodeId: id });

  if (doc.nodes.length && !doc.nodes.some((n) => n.kind === 'trigger')) warn(['nodes'], 'The flow has no Start node.');
  doc.nodes.forEach((n, i) => {
    if (n.kind === 'trigger') return;
    const ports = resolveFlowPorts(n, lib);
    for (const input of ports.inputs) {
      if (input.optional) continue;
      if (!doc.edges.some((e) => e.to === `${n.id}.${input.name}`)) warn(['nodes', i], `${flowNodeTitle(n)}.${input.name} is not connected.`, { nodeId: n.id });
    }
  });
  return issues;
}

/** Errors and warnings by node id, for badges on the canvas. */
export function flowIssuesByNode(issues: FlowIssue[]): Record<string, FlowIssue[]> {
  const out: Record<string, FlowIssue[]> = {};
  for (const i of issues) if (i.nodeId) (out[i.nodeId] ??= []).push(i);
  return out;
}

// ---------------------------------------------------------------------------
// Hub API (module 'flows')
// ---------------------------------------------------------------------------

export const FLOW_OUTLINE_MAX = 60;

export interface FlowOutline {
  nodes: { id: string; kind: string }[];
  /** [from node, to node] */
  edges: [string, string][];
}

/** A flow's shape, small enough to list: what the thumbnails draw. */
export function flowOutline(doc: Pick<FlowDoc, 'nodes' | 'edges'>): FlowOutline {
  const nodes = doc.nodes.slice(0, FLOW_OUTLINE_MAX).map((n) => ({ id: n.id, kind: n.kind }));
  const ids = new Set(nodes.map((n) => n.id));
  const edges: [string, string][] = [];
  for (const e of doc.edges) {
    const a = parseFlowRef(e.from)?.node;
    const b = parseFlowRef(e.to)?.node;
    if (a && b && ids.has(a) && ids.has(b) && !edges.some(([x, y]) => x === a && y === b)) edges.push([a, b]);
  }
  return { nodes, edges };
}

/** One flow file as GET /api/flows lists it. */
export interface FlowSummary {
  id: string;
  name: string;
  description?: string;
  workflow?: string;
  tags: string[];
  /** File name under <hub dir>/flows/. */
  file: string;
  format: FlowFormat;
  /** Content hash: changes with every edit, in Orgus or on disk. */
  rev: string;
  updatedAt: Ts;
  nodes: number;
  edges: number;
  gates: number;
  /** Subflows it uses (flow ids). */
  uses: string[];
  interface: FlowInterface;
  /** The graph's shape for a thumbnail: node kinds and node-to-node edges (first FLOW_OUTLINE_MAX nodes). */
  outline: FlowOutline;
  errors: number;
  warnings: number;
}

/** A file in the flows folder that could not be read as a flow. */
export interface FlowFileProblem {
  id: string;
  file: string;
  format: FlowFormat;
  rev: string;
  updatedAt: Ts;
  message: string;
  line?: number;
}

export interface FlowsListResponse {
  flows: FlowSummary[];
  broken: FlowFileProblem[];
  /** Where the files are, for people who edit them by hand. */
  dir: string;
}

/** GET /api/flows/:id: the text as stored, and the flow when it reads. */
export interface FlowRecord {
  id: string;
  file: string;
  format: FlowFormat;
  rev: string;
  updatedAt: Ts;
  text: string;
  flow: FlowDoc | null;
  issues: FlowIssue[];
}

/** PUT /api/flows/:id. baseRev: the rev the edit started from (null for a new file); a mismatch answers 409. */
export interface FlowSaveRequest {
  text: string;
  format: FlowFormat;
  baseRev: string | null;
}

/** POST /api/flows: from text, or a fresh flow with this id and name (the web builds the text). */
export interface FlowCreateRequest {
  text: string;
  format: FlowFormat;
}

export interface FlowRevision {
  rev: string;
  at: Ts;
  actor: string;
  /** What happened: saved in Orgus, changed on disk, deleted. */
  reason: 'save' | 'disk' | 'delete' | 'create';
  size: number;
}

/** GET /api/flows/:id/revisions/:rev */
export interface FlowRevisionText {
  rev: string;
  at: Ts;
  text: string;
  format: FlowFormat;
}

/**
 * state.ext.flows: small, so the aggregate stays light. Kept live: after any change the hub
 * sends SSE 'ext' { ext: 'flows', type: 'state', data: FlowsState }, and `rev` goes up, so a
 * view re-reads /api/flows when it changes instead of polling.
 */
export interface FlowsState {
  count: number;
  broken: number;
  updatedAt: Ts;
  /** Goes up by one on every change the hub sees (0 before the first). */
  rev: number;
}

/** SSE 'ext' { ext: 'flows', type: 'changed', data } after any save, delete or edit on disk. */
export interface FlowsChanged {
  ids: string[];
  at: Ts;
  /** The FlowsState rev after this change. */
  rev: number;
}
